import {computed, ref, shallowRef, watch, type Ref} from 'vue'
import {CalendarDateTime, type DateValue} from '@internationalized/date'
import type {Task, TaskFolder} from '@backend/types'
import {useAppStore} from '@/stores/app'
import {dateValueToApiString} from './useDateConversion'
import {durationOptions, energyOptions, reminderPresets, folderOptions} from './useTaskEditForm'

export {durationOptions, energyOptions, reminderPresets, folderOptions}

interface CreateFormOptions {
  defaultFolder: string | undefined
  defaultProjectId: string | undefined
  defaultFocused: boolean | undefined
}

const properFolders = ['inbox', 'next', 'scheduled', 'waiting', 'someday', 'reference', 'archived']

export function useTaskCreateForm(options: Ref<CreateFormOptions>) {
  const appStore = useAppStore()

  const title = ref('')
  const notes = ref('')
  const folder = ref<TaskFolder>('inbox')
  const dueDate = shallowRef<DateValue | undefined>()
  const duration = ref('')
  const energy = ref('')
  const delegatedTo = ref('')
  const projectId = ref<string>('')
  const selectedTags = ref<string[]>([])
  const reminders = ref<string[]>([])
  const autoComplete = ref(false)
  const recurringSchedule = ref('')
  const recurringCondition = ref('')
  const recurringStartAt = shallowRef<DateValue | undefined>()
  const recurringEndAt = shallowRef<DateValue | undefined>()
  const isAddingNewContact = ref(false)
  const newContactName = ref('')
  const isSaving = ref(false)

  const canSave = computed(() => title.value.trim().length >= 2 && !isSaving.value)
  const canConfigureAutoComplete = computed(() =>
    Boolean(dueDate.value || (recurringSchedule.value && recurringCondition.value))
  )

  watch(canConfigureAutoComplete, (value) => {
    if (!value) {
      autoComplete.value = false
    }
  })

  function resetForm() {
    title.value = ''
    notes.value = ''
    duration.value = ''
    energy.value = ''
    delegatedTo.value = ''
    selectedTags.value = []
    reminders.value = []
    autoComplete.value = false
    dueDate.value = undefined
    recurringSchedule.value = ''
    recurringCondition.value = ''
    recurringStartAt.value = undefined
    recurringEndAt.value = undefined
    isAddingNewContact.value = false
    newContactName.value = ''

    // Apply defaults
    const opts = options.value
    let defaultFolder: TaskFolder = 'inbox'
    if (opts.defaultProjectId) {
      defaultFolder = 'next'
      projectId.value = opts.defaultProjectId
    } else {
      projectId.value = ''
    }
    folder.value =
      opts.defaultFolder && properFolders.includes(opts.defaultFolder)
        ? (opts.defaultFolder as TaskFolder)
        : defaultFolder
  }

  function addReminder(value: string) {
    if (value && !reminders.value.includes(value)) {
      reminders.value.push(value)
    }
  }

  function removeReminder(index: number) {
    reminders.value.splice(index, 1)
  }

  function handleNewContact() {
    if (newContactName.value.trim()) {
      delegatedTo.value = newContactName.value.trim()
    }
    isAddingNewContact.value = false
    newContactName.value = ''
  }

  async function saveTask(): Promise<{success: boolean; error?: string}> {
    isSaving.value = true
    try {
      const taskData: Partial<Task> & {title: string; folder: TaskFolder} = {
        title: title.value.trim(),
        folder: folder.value,
        notes: notes.value || undefined,
        duration: duration.value || null,
        energy: energy.value || null,
        delegated_to: delegatedTo.value || undefined,
        project_id: projectId.value ? Number(projectId.value) : null,
        tags: selectedTags.value.length > 0 ? selectedTags.value : undefined,
        focused: options.value.defaultFocused || false,
        auto_complete: canConfigureAutoComplete.value ? autoComplete.value : false,
        reminders: reminders.value.filter((r) => r.trim()),
      }

      if (recurringSchedule.value && recurringCondition.value) {
        taskData.recurring = {
          schedule: recurringSchedule.value,
          condition: recurringCondition.value,
          start_at: recurringStartAt.value ? dateValueToApiString(recurringStartAt.value) : undefined,
          end_at: recurringEndAt.value ? dateValueToApiString(recurringEndAt.value) : undefined,
        }
      }

      const instance = dueDate.value
        ? {
            due_date: dateValueToApiString(dueDate.value),
            done: false,
          }
        : undefined

      await appStore.createTask(taskData, instance)
      return {success: true}
    } catch (error) {
      console.error('Failed to create task:', error)
      return {success: false, error: String(error)}
    } finally {
      isSaving.value = false
    }
  }

  return {
    title,
    notes,
    folder,
    dueDate,
    duration,
    energy,
    delegatedTo,
    projectId,
    selectedTags,
    reminders,
    autoComplete,
    canConfigureAutoComplete,
    recurringSchedule,
    recurringCondition,
    recurringStartAt,
    recurringEndAt,
    isAddingNewContact,
    newContactName,
    isSaving,
    canSave,
    resetForm,
    addReminder,
    removeReminder,
    handleNewContact,
    saveTask,
  }
}
